import { FormStepType } from "../types";
import { countries } from "../options/countries";
import { states } from "../options/states";
import { gender } from "../options/gender";
import { height, inches, hairColor, eyeColor } from "../options/physicalAttributes";
import { bookType } from "../options/passport";
import getYesNoOptions from "../options/yes_no_options";

export const applicant_details: FormStepType = {
  id: 1,
  name: "applicant-details",
  title: "1. Applicant Details",
  groups: [
    {
      name: "passport format",
      fields: [
        {
          component: "Radio",
          name: "bookType",
          label: "Do you need a regular or large passport book?",
          reviewLabel: "Selected Book Type",
          type: "radio",
          validation: {
            required: "Please select the type of book.",
          },
          cols: 12,
          options: bookType,
          placeholder: "Select book type",
          defaultValue: "regularbook",
          tooltip:
            "The large book option is for those who frequently travel abroad during the passport validity period, and is recommended for applicants who have previously required the addition of visa pages",
        },
      ],
    },
    {
      name: "name & birth",
      hasSeparationLine: true,
      fields: [
        {
          component: "TextInput",
          name: "firstName",
          label: "First Name",
          type: "text",
          maxLength: 17,
          validation: {
            required: "Please enter the first name.",
            maxLength: 17,
            minLength: {
              value: 2,
              message: "The first name must have at least two characters.",
            },
          },
          cols: 4,
          placeholder: "",
        },
        {
          component: "TextInput",
          name: "middleName",
          label: "Middle Name/s (Optional)",
          type: "text",
          maxLength: 16,
          validation: {
            maxLength: 16,
            minLength: {
              value: 2,
              message: "The middle name must have at least two characters.",
            },
          },
          cols: 4,
          placeholder: "",
        },
        {
          component: "TextInput",
          name: "lastName",
          label: "Last Name",
          type: "text",
          maxLength: 21,
          validation: {
            required: "Please enter the last name.",
            maxLength: 21,
            minLength: {
              value: 2,
              message: "The last name must have at least two characters.",
            },
          },
          cols: 4,
          placeholder: "",
        },
        {
          component: "TextInput",
          name: "suffix",
          label: "Name Suffix (Optional)",
          type: "text",
          maxLength: 3,
          validation: {
            maxLength: {
              value: 3,
              message: "The name suffix must have maximum three characters.",
            },
          },
          cols: 4,
          placeholder: "e.g. Jr. or Sr.",
        },
        {
          component: "Date",
          name: "birthDate",
          label: "Date of Birth",
          type: "date",
          validation: { required: "Please select a date." },
          placeholder: "Please select a date.",
          maxElegibleDate: "today",
          cols: 4,
        },
        {
          component: "TextInput",
          name: "birthCity",
          label: "City of Birth",
          type: "text",
          maxLength: 17,
          validation: {
            required: "Please enter the city.",
            maxLength: 17,
            minLength: {
              value: 2,
              message: "The city of birth must have at least two characters.",
            },
          },
          cols: 4,
          placeholder: "",
        },
        {
          component: "Select",
          name: "birthCountry",
          label: "Country of Birth",
          type: "select",
          validation: {
            required: "Please enter the country.",
          },
          cols: 4,
          options: countries,
        },
        {
          component: "Select",
          name: "birthState",
          label: "State/Territory of Birth",
          type: "select",
          validation: {
            required: "Please enter the state.",
          },
          cols: 4,
          placeholder: "Select a State",
          options: states,
          condition: {
            field: "birthCountry",
            value: "US",
          },
        },
        {
          component: "Radio",
          name: "sex",
          label: "Gender",
          class: "radio--switch",
          type: "radio",
          tooltip:
            "You can select Male or Female. Please note that the U.S. government no longer accepts the 'X' (unspecified or other gender identity) option.",
          validation: {
            required: "Please enter the gender.",
          },
          cols: 4,
          options: gender,
        },
        {
          component: "TextInput",
          name: "ssn",
          label: "Social Security Number (SSN)",
          tooltip:
            "You must use your Social Security number (SSN) to apply for your passport if you have one.<br/><br/>If you haven’t been issued your SSN yet, simply enter zeros in this field.",
          type: "text",
          mask: "999-99-9999",
          validation: {
            pattern: {
              value: /^\d{3}-\d{2}-\d{4}$/,
              message: "Please enter a valid SSN/ITIN number.",
            },
            required: "Please enter the SSN/ITIN.",
            minLength: {
              value: 11,
              message: "The social security number must have at least nine characters.",
            },
          },
          cols: 4,
          placeholder: "XXX-XX-XXXX",
        },
      ],
    },
    {
      name: "",
      hasSeparationLine: true,
      fields: [
        {
          component: "Select",
          name: "feet",
          label: "Height",
          type: "select",
          validation: {
            required: "Please enter the height.",
          },
          cols: 2,
          options: height,
          placeholder: "Feet",
        },
        {
          component: "Select",
          name: "inches",
          label: "Inches",
          type: "select",
          validation: {
            required: "Please enter the inches.",
          },
          cols: 2,
          options: inches,
          placeholder: "Inches",
        },
        {
          component: "Select",
          name: "hairColor",
          label: "Hair Color",
          type: "select",
          validation: {
            required: "Please enter the hair color.",
          },
          cols: 4,
          options: hairColor,
          placeholder: "Select hair color",
        },
        {
          component: "Select",
          name: "eyeColor",
          label: "Eye Color",
          type: "select",
          validation: {
            required: "Please enter the eye color.",
          },
          cols: 4,
          options: eyeColor,
          placeholder: "Select eye color",
        },
        {
          component: "TextInput",
          name: "occupation",
          label: "Occupation",
          type: "text",
          maxLength: 20,
          validation: {
            required: "Please enter the occupation.",
            maxLength: 20,
            minLength: {
              value: 2,
              message: "The occupation must have at least two characters.",
            },
          },
          cols: 6,
          placeholder: "",
          tooltip:
            "If you are an adult you must enter your occupation in this form.<br/><br/>If you don’t have an occupation you may enter “unemployed” in this section.<br/><br/>For children and minors, you may enter “student” or “child” in the occupation field.",
        },
        {
          component: "TextInput",
          name: "employer",
          label: "Employer or School (Optional)",
          type: "text",
          maxLength: 20,
          validation: {
            maxLength: 20,
            minLength: {
              value: 2,
              message: "The employer or school must have at least two characters.",
            },
          },
          cols: 6,
          placeholder: "",
          tooltip:
            "Enter the name of the company you work at or the school you attend as a student.",
        },
      ],
    },
    {
      name: "changed-name",
      hasSeparationLine: true,
      fields: [
        {
          component: "Radio",
          name: "hasChangedName",
          label: "Has your name ever changed?",
          subLabel: "e.g. maiden name, previous marriage name, or name before legal change.",
          type: "radio",
          validation: {
            required: "Please enter your answer.",
          },
          defaultValue: "no",
          options: getYesNoOptions({}),
        },
        {
          component: "TextInput",
          name: "nameA",
          label: "Former Name A. (Optional)",
          type: "text",
          maxLength: 17,
          validation: {
            maxLength: 17,
            minLength: {
              value: 1,
              message: "The former name A. must have at least one character.",
            },
          },
          cols: 6,
          placeholder: "",
          condition: {
            field: "hasChangedName",
            value: "yes",
          },
        },
        {
          component: "TextInput",
          name: "nameB",
          label: "Former Name B. (Optional)",
          type: "text",
          maxLength: 17,
          validation: {
            maxLength: 17,
            minLength: {
              value: 1,
              message: "The former name B. must have at least one character.",
            },
          },
          cols: 6,
          placeholder: "",
          condition: {
            field: "hasChangedName",
            value: "yes",
          },
        },
      ],
    },
  ],
};
